import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import SaveAltOutlinedIcon from '@mui/icons-material/SaveAltOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { ROUTES } from './routes';
import openAiWordmark from './assets/openai-wordmark.webp';
import alexPortrait from './assets/guide-bot/alex/alex-hopeful.png';

const features = [
  {
    title: 'Journey steps and cards',
    description: 'Map the buying journey into visible steps instead of keeping the whole goal in your head.',
    icon: <DashboardCustomizeOutlinedIcon />,
  },
  {
    title: 'Action items inside each step',
    description: 'Capture the concrete tasks, notes, decisions, and blockers that move each step forward.',
    icon: <TaskAltOutlinedIcon />,
  },
  {
    title: 'Local-first saving',
    description: 'Work in your browser, save manually, and use cloud save when you are signed in.',
    icon: <CloudDoneOutlinedIcon />,
  },
  {
    title: 'AI plan import',
    description: 'Reuse useful output from ChatGPT, Claude, Gemini, or other tools by importing it into your board.',
    icon: <FileUploadOutlinedIcon />,
  },
  {
    title: 'JSON import and export',
    description: 'Keep a portable copy of your journey and move structured plans in or out when needed.',
    icon: <SaveAltOutlinedIcon />,
  },
  {
    title: 'Progress and status tracking',
    description: 'Mark actions active, deferred, completed, relevant, irrelevant, or unknown as priorities change.',
    icon: <FlagOutlinedIcon />,
  },
];

const howItWorks = [
  'Create or load your journey.',
  'Break the goal into steps.',
  'Import useful AI output.',
  'Track what is active, deferred, completed, relevant, or irrelevant.',
  'Save locally or to cloud.',
];

const mediaPlaceholders = [
  'Placeholder: Journey board screenshot',
  'Placeholder: Mobile journey navigation',
  'Placeholder: AI import flow',
  'Placeholder: Action status tracking',
];

export const isProductMediaEnabled = (
  value = process.env.REACT_APP_ENABLE_PRODUCT_MEDIA,
) => value === 'true';

function SectionHeading({ eyebrow, title, body }) {
  return (
    <Box sx={{ maxWidth: 780, mx: 'auto', textAlign: 'center' }}>
      {eyebrow && (
        <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>
          {eyebrow}
        </Typography>
      )}
      <Typography variant="h3" component="h2" sx={{ mt: 0.5, fontWeight: 900 }}>
        {title}
      </Typography>
      {body && (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
          {body}
        </Typography>
      )}
    </Box>
  );
}

function FeatureCard({ feature }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', textAlign: 'left' }}>
      <CardContent>
        <Stack spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              color: 'primary.main',
              bgcolor: 'primary.50',
              border: '1px solid',
              borderColor: 'primary.light',
            }}
          >
            {feature.icon}
          </Box>
          <Typography variant="h6" component="h3" sx={{ fontWeight: 800 }}>
            {feature.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {feature.description}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

function MediaPlaceholder({ label }) {
  return (
    <Card
      variant="outlined"
      sx={{
        minHeight: 190,
        height: '100%',
        bgcolor: 'grey.50',
        borderStyle: 'dashed',
      }}
    >
      <CardContent sx={{ height: '100%' }}>
        <Stack sx={{ minHeight: 150, height: '100%', justifyContent: 'center', alignItems: 'center' }} spacing={1}>
          <DashboardCustomizeOutlinedIcon color="action" />
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Replace with product media when screenshots are ready.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function LandingPage() {
  const { authState } = useAuth();
  const isSignedIn = Boolean(authState.user);
  const startPlanningRoute = isSignedIn ? ROUTES.mainView : ROUTES.login;

  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary', textAlign: 'left' }}>
      {process.env.REACT_APP_GUIDE_BOT_ENABLED === 'true' && (
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
          <Card
            component="section"
            aria-labelledby="meet-alex-heading"
            sx={{
              borderRadius: 4,
              border: '1px solid #b8d8d2',
              background: 'linear-gradient(120deg, #f7f3eb 0%, #e9f5f2 100%)',
              boxShadow: '0 8px 28px rgba(23, 83, 77, 0.10)',
              overflow: 'hidden',
            }}
          >
            <CardActionArea
              component={RouterLink}
              to={ROUTES.guideBot}
              aria-label="Meet Alex · Guide a fictional first-home buyer"
              sx={{ p: { xs: 2.5, sm: 3 }, '&:hover .alex-card-cta': { bgcolor: '#164e48' } }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 4 }} alignItems="center">
                <Box
                  component="img"
                  src={alexPortrait}
                  alt="Alex, the LEGO-style first-home buyer"
                  width={210}
                  height={210}
                  sx={{ width: { xs: 144, sm: 210 }, height: { xs: 144, sm: 210 }, borderRadius: 3, flexShrink: 0 }}
                />
                <Stack spacing={1.5} sx={{ alignItems: { xs: 'center', sm: 'flex-start' }, textAlign: { xs: 'center', sm: 'left' } }}>
                  <Typography variant="overline" sx={{ color: '#205e55', fontWeight: 800, letterSpacing: 1.5, lineHeight: 1.5 }}>
                    An interactive story
                  </Typography>
                  <Typography id="meet-alex-heading" component="h2" variant="h3" sx={{ fontWeight: 900, color: '#183f3a' }}>
                    Meet Alex
                  </Typography>
                  <Typography sx={{ color: '#34534d', maxWidth: 630 }}>
                    Guide a fictional first-home buyer through saving, moving out and everyday life.
                    Ask questions, help him choose a next step and see what happens.
                  </Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" spacing={1.5}>
                    <Box
                      component="span"
                      className="alex-card-cta"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, bgcolor: '#205e55', color: '#fff', borderRadius: 2, px: 3, py: 1.25, fontWeight: 700 }}
                    >
                      Meet Alex <ArrowForwardIcon fontSize="small" />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#34534d' }}>No account needed</Typography>
                  </Stack>
                </Stack>
              </Stack>
            </CardActionArea>
          </Card>
        </Container>
      )}
      <Box
        component="section"
        sx={{
          bgcolor: '#f7fafc',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 5, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <Chip label="Journey Planner for First Home Buyers" color="primary" sx={{ alignSelf: 'flex-start' }} />
                <Box>
                  <Typography variant="h2" component="h1" sx={{ fontWeight: 900, maxWidth: 760 }}>
                    Turn your first-home goal into a journey you can actually follow.
                  </Typography>
                  <Typography variant="h6" component="p" color="text.secondary" sx={{ mt: 2, maxWidth: 680 }}>
                    Journey Planner helps you break a big first-home-buyer goal into clear steps,
                    useful actions, and a plan you can keep improving over time. Instead of losing
                    advice across chats, notes, spreadsheets, and browser tabs, you get one
                    structured place to track what matters next.
                  </Typography>
                </Box>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  role="group"
                  aria-label="Journey planning options"
                  sx={{ flexWrap: { sm: 'wrap' }, rowGap: 1.5, '& .MuiButton-root': { minHeight: 44 } }}
                >
                  <Button
                    component={RouterLink}
                    to={startPlanningRoute}
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                  >
                    Start planning
                  </Button>
                  <Button
                    component={RouterLink}
                    to={ROUTES.templates}
                    variant="outlined"
                    size="large"
                    startIcon={<CollectionsBookmarkOutlinedIcon />}
                    data-testid="landing-use-template"
                  >
                    Use a template
                  </Button>
                  <Button
                    component={RouterLink}
                    to={ROUTES.journeyImport}
                    variant="outlined"
                    size="large"
                  >
                    Import an AI plan
                  </Button>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  minHeight: { xs: 300, md: 420 },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  p: { xs: 2, sm: 3 },
                  boxShadow: 3,
                }}
              >
                <Stack spacing={2} sx={{ height: '100%' }}>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <Chip size="small" label="Deposit" />
                    <Chip size="small" label="Borrowing power" />
                    <Chip size="small" label="Suburbs" />
                    <Chip size="small" label="Contracts" />
                  </Stack>
                  <Divider />
                  {['Active: confirm borrowing range', 'Deferred: compare inspection providers', 'Completed: collect payslips'].map((item) => (
                    <Box
                      key={item}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2,
                        bgcolor: 'grey.50',
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        {item}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Placeholder journey board preview
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Stack component="section" spacing={3} sx={{ py: { xs: 5, md: 7 } }}>
          <SectionHeading
            eyebrow="The problem"
            title="First-home buying has too many moving parts to track casually."
            body="Buying your first home is not one decision. It is a chain of trade-offs: savings, borrowing power, suburbs, grants, inspections, offers, contracts, settlement, and the life changes around them. Advice gets scattered. Priorities shift. AI chats produce useful ideas, but those ideas are easy to lose."
          />
        </Stack>

        <Box component="section" sx={{ py: { xs: 5, md: 7 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <SectionHeading
                eyebrow="The solution"
                title="One living journey board."
                body="Journey Planner gives you a living journey board. Add steps, track actions, import useful AI-generated plans, mark what is relevant, defer what is not ready, and keep moving toward the next useful decision."
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                {features.map((feature) => (
                  <Grid item xs={12} sm={6} key={feature.title}>
                    <FeatureCard feature={feature} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>

        <Box component="section" sx={{ py: { xs: 5, md: 7 } }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
              <SectionHeading
                eyebrow="How it works"
                title="Build a path you can keep refining."
                body="Use the board as the centre of the journey, then bring in the supporting tools when they help."
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Stack spacing={2}>
                {howItWorks.map((step, index) => (
                  <Stack direction="row" spacing={2} alignItems="flex-start" key={step}>
                    <Chip label={index + 1} color="primary" />
                    <Typography variant="h6" component="p" sx={{ fontWeight: 700 }}>
                      {step}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {isProductMediaEnabled() ? (
          <Box component="section" data-testid="landing-product-media" sx={{ py: { xs: 5, md: 7 } }}>
            <Stack spacing={3}>
              <SectionHeading
                eyebrow="Product media"
                title="Show the journey board in context."
                body="These blocks reserve space for real screenshots or short walkthroughs without blocking the public launch."
              />
              <Grid container spacing={2}>
                {mediaPlaceholders.map((label) => (
                  <Grid item xs={12} sm={6} md={3} key={label}>
                    <MediaPlaceholder label={label} />
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Box>
        ) : null}

        <Box component="section" sx={{ py: { xs: 5, md: 7 } }}>
          <Alert
            severity="info"
            icon={<SaveAltOutlinedIcon />}
            sx={{ alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="h5" component="h2" sx={{ fontWeight: 900, mb: 1 }}>
              You stay in control of your journey.
            </Typography>
            <Typography variant="body1">
              Work locally in your browser, export your journey as JSON, or save to cloud when you
              sign in. If your local and cloud versions differ, the app warns you before you
              overwrite anything.
            </Typography>
          </Alert>
        </Box>

        <Box component="section" data-testid="landing-primary-cta" sx={{ py: { xs: 5, md: 8 }, textAlign: 'center' }}>
          <Stack spacing={2.5} alignItems="center">
            <Typography variant="h3" component="h2" sx={{ fontWeight: 900 }}>
              Start your first-home journey
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 660 }}>
              Open the journey board, create your first steps, and keep the useful parts of your
              planning work in one structured place.
            </Typography>
            <Button
              component={RouterLink}
              to={startPlanningRoute}
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
            >
              Start planning
            </Button>
          </Stack>
        </Box>

        <Box
          component="section"
          aria-labelledby="chatgpt-feature-heading"
          data-testid="landing-chatgpt-feature"
          sx={{ pt: { xs: 2, md: 3 }, pb: { xs: 5, md: 7 } }}
        >
          <Card
            variant="outlined"
            sx={{
              bgcolor: 'grey.50',
              borderColor: 'divider',
              boxShadow: 0,
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Stack spacing={1.5}>
                    <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>
                      ChatGPT
                    </Typography>
                    <Typography id="chatgpt-feature-heading" variant="h4" component="h2" sx={{ fontWeight: 850 }}>
                      Use JourneyBot in ChatGPT
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760 }}>
                      JourneyBot is available as a custom GPT in ChatGPT, so you can turn a
                      conversation into structured goals, decisions, action items, blockers, and
                      next steps.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      JourneyBot is an independent product and is not affiliated with, endorsed by,
                      or sponsored by OpenAI.
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack alignItems={{ xs: 'stretch', md: 'flex-end' }}>
                    <Button
                      component="a"
                      href="https://chatgpt.com/g/g-6a543064ab5081918b030dc9cddf51ea-journeyapp"
                      aria-label="Open JourneyBot in ChatGPT"
                      variant="outlined"
                      size="large"
                      startIcon={(
                        <Box
                          component="img"
                          src={openAiWordmark}
                          alt="OpenAI logo"
                          sx={{ width: 48, height: 'auto', display: 'block' }}
                        />
                      )}
                      endIcon={<ArrowForwardIcon />}
                      sx={{ minHeight: 44, alignSelf: { xs: 'stretch', sm: 'flex-start', md: 'flex-end' } }}
                    >
                      Open JourneyBot in ChatGPT
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
}
